import { parseCsv } from "@/lib/csv";
import { isTableArtifact } from "@/lib/artifacts";
import { projectsApi, type Artifact } from "@/services/projectsApi";

/** Escapes a value for use inside a markdown table cell. Brackets are escaped
 *  too so a cell mentioning e.g. "[model.xlsx]" isn't read as a citation. */
const escapeCell = (s: string) =>
  s.replace(/\\/g, "\\\\").replace(/\|/g, "\\|").replace(/\[/g, "\\[").replace(/\]/g, "\\]").replace(/\r?\n/g, " ").trim();

const escapeAlt = (s: string) => s.replace(/[[\]]/g, "").replace(/\r?\n/g, " ").trim();

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** The caption line, carrying the artefact's identity so anything downstream
 *  can tell which artefact a block came from.
 *
 *  Raw HTML rather than `**bold**` because markdown has no attribute slot, and
 *  the caption is the only part of the block that can hold one without
 *  disturbing the content. Wrapping the whole block in a <figure> was the
 *  obvious alternative and does not work: a markdown table inside a raw HTML
 *  block stops being parsed as a table, by the web app and the export service
 *  alike.
 *
 *  The identity is left off when the artefact has no upload version. A value
 *  that cannot be compared later is worse than none, because it would be
 *  reported as a confident mismatch; those artefacts go untracked until the
 *  next model upload gives them a version.
 */
export function captionLine(artifact: Artifact): string {
  const name = `<strong>${escapeHtml(artifact.name)}</strong>`;
  if (artifact.uploadVersion === null || artifact.uploadVersion === undefined) {
    return `<p>${name}</p>`;
  }
  return (
    `<p data-artifact-uid="${escapeHtml(artifact.uid)}"` +
    ` data-artifact-version="${artifact.uploadVersion}">${name}</p>`
  );
}

export function csvToMarkdownTable(csv: string): string {
  const rows = parseCsv(csv).filter((r) => r.some((c) => c.trim() !== ""));
  if (rows.length === 0) return "";
  const width = Math.max(...rows.map((r) => r.length));
  const pad = (r: string[]) => [...r, ...Array(width - r.length).fill("")].map(escapeCell);
  const [header, ...body] = rows;
  return [
    `| ${pad(header).join(" | ")} |`,
    `| ${Array(width).fill("---").join(" | ")} |`,
    ...body.map((r) => `| ${pad(r).join(" | ")} |`),
  ].join("\n");
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/** Builds the markdown block that shows an artefact below a report section:
 *  a bold caption followed by the table (from CSV) or the image/chart, embedded
 *  as a data URL — the file endpoint needs an auth header, which neither an
 *  <img> nor the Word export can send. Never saved into the section text, so
 *  the section always shows the artefact's current version. */
export async function buildArtifactMarkdown(projectId: string, artifact: Artifact): Promise<string> {
  if (!artifact.blobName) return "";
  const blob = await projectsApi.getArtifactFile(projectId, artifact.uid, artifact.updatedAt);
  const caption = captionLine(artifact);

  if (isTableArtifact(artifact)) {
    const table = csvToMarkdownTable(await blob.text());
    return table ? `${caption}\n\n${table}` : "";
  }

  // Blob type can come back generic; prefer the artefact's recorded content type.
  const typed = artifact.contentType?.startsWith("image/") && blob.type !== artifact.contentType
    ? new Blob([blob], { type: artifact.contentType })
    : blob;
  if (!typed.type.startsWith("image/")) return "";
  const dataUrl = await blobToDataUrl(typed);
  return `${caption}\n\n![${escapeAlt(artifact.name)}](${dataUrl})`;
}

/** Query options for one artefact's markdown. Keyed by `updatedAt`, so a new
 *  version is fetched once and cached; shared by rendering and the Word export.
 *  A failing artefact is logged and resolves empty rather than erroring, so it
 *  is skipped instead of retried or blocking the rest of the section. */
export const artifactMarkdownQuery = (projectId: string, artifact: Artifact) => ({
  queryKey: ["projects", projectId, "artifact-markdown", artifact.uid, artifact.updatedAt] as const,
  queryFn: () =>
    buildArtifactMarkdown(projectId, artifact).catch((err) => {
      console.error("Failed to load artifact for section:", artifact.uid, err);
      return "";
    }),
  staleTime: Infinity,
});
